import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Input, Modal, Popconfirm, Row, Select, Space, Spin, Statistic, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, WarningOutlined, WhatsAppOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventoCobrancaAdminService, eventoInscricaoAdminService, eventoService, unidadeService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import ConsolidarCobrancasModal from './ConsolidarCobrancasModal';
import SecaoCobrancasConsolidadas from './SecaoCobrancasConsolidadas';
import {
  ACAO_FINANCEIRA,
  LIMITE_LOTE_EMITIR_BOLETOS,
  LIMITE_LOTE_GERAR_COBRANCAS,
  agruparPorGroupId,
  calcularQuantidadeEstimadaLote,
  calcularValorSelecionadoEmissaoLote,
  chaveAcaoEmAndamento,
  chavesExpandidasAposRecarregar,
  construirMensagemWhatsAppIndividual,
  corStatusItemLoteEmissao,
  corStatusResumido,
  desmarcarTodasDoGrupo,
  grupoElegivelParaGerarCobranca,
  idsElegiveisDoGrupo,
  indexarSugestoesConsolidacao,
  labelStatusItemLoteEmissao,
  labelStatusResumido,
  loteConcluidoSemProblemas,
  loteEmissaoConcluidoSemProblemas,
  mensagemErroAcaoFinanceira,
  mensagemFeedbackCancelamento,
  mensagemFeedbackEmissao,
  mensagemFeedbackVerificacao,
  mensagemFeedbackVerificarPagamentoGrupo,
  mostrarBotaoCancelarCobranca,
  normalizarTelefoneWhatsApp,
  parcelaElegivelParaEmitirBoletoLote,
  parcelaElegivelParaRecobranca,
  parcelaElegivelParaWhatsApp,
  podarSelecaoEmissaoLote,
  resultadosProblematicosLote,
  resultadosProblematicosLoteEmissao,
  resumoCompactoParcelas,
  selecaoDentroDoLimiteLote,
  selecionarElegiveisDoGrupo,
} from './cobrancasEventosAcoes';

const { RangePicker } = DatePicker;

// Tela "Eventos > Cobrancas de Eventos" (Fase B.5c.3b/B.5c.3c.3b, agora
// AGRUPADA por inscricao desde a Fase 2B) -- consome GET /api/eventos/
// cobrancas/agrupado (Fase 2A backend, read-only) e integra as acoes
// financeiras administrativas: Gerar cobranca (nivel do grupo, desde
// B.5c.1), Emitir boleto (B.5c.3c.1), Verificar situacao (B.5c.3c.2) e
// Cancelar/verificar cancelamento (por parcela, dentro da linha
// expandida). A visao plana (GET /api/eventos/cobrancas) permanece no
// backend e no service (`eventoCobrancaAdminService.listar`) por
// compatibilidade, mas esta tela nao a consome mais -- decisao de
// produto (Fase 2B): substituir visualmente a tabela plana, sem toggle
// Agrupado/Detalhado nem duas telas concorrentes.
//
// PRINCIPIO CENTRAL (Fase B.5c.3c.3b, preservado integralmente): a
// decisao de qual acao financeira primaria mostrar depende
// EXCLUSIVAMENTE de `parcela.acao_financeira` (campo semantico calculado
// pelo backend) -- NUNCA reconstruida a partir de status_emissao/
// status_cobranca/status_participacao/status_inscricao/provider/
// data_vencimento/linha_digitavel (ver cobrancasEventosAcoes.js). Os
// endpoints POST continuam sendo a UNICA fonte de verdade no momento do
// clique -- sempre revalidam integralmente no backend. `acao_financeira`
// e' apenas uma dica/capacidade de UI, nunca uma autorizacao. O mesmo
// vale para `status_resumido` do grupo (Fase 2A): e' SOMENTE
// apresentacao, o frontend nunca recalcula a precedencia entre os 10
// codigos (ver STATUS_RESUMIDO_GRUPO_VALIDOS no backend).
const STATUS_COBRANCA_COLORS = {
  SEM_COBRANCA: 'default',
  ABERTA: 'blue',
  VENCIDA: 'red',
  PAGA: 'green',
  CANCELADA: 'default',
  // Fase cancelamento-canonico (frontend) -- estado transitorio (em
  // andamento OU aguardando revisao manual de conflito de pagamento,
  // ver acao_financeira=NENHUMA nesse ultimo caso), nunca deve parecer
  // sucesso (verde) nem falha definitiva (vermelho).
  CANCELANDO: 'processing',
  // Fase Eventos-Recobranca 3 -- estado DERIVADO de read-model (a unica
  // charge da parcela foi CANCELADA; a obrigacao continua e e' recobravel
  // via "Gerar nova cobranca"). Laranja: exige acao, nunca sucesso/falha.
  RECOBRANCA_NECESSARIA: 'orange',
};

const STATUS_COBRANCA_LABELS = {
  SEM_COBRANCA: 'Não gerada',
  ABERTA: 'Em aberto',
  VENCIDA: 'Vencida',
  PAGA: 'Paga',
  CANCELADA: 'Cancelada',
  CANCELANDO: 'Cancelamento em andamento',
  RECOBRANCA_NECESSARIA: 'Recobrança necessária',
};

// status_emissao (Secao 10/17/31 da instrucao): FALHA_TECNICA e
// FALHA_PROVIDER sao unificados visualmente como "Falha na emissao" na
// TABELA (nenhuma acao diferente existe entre eles nesta fase), mas o
// FILTRO precisa preservar os dois valores separados -- o backend nao
// aceita um valor inventado "FALHA" (ver STATUS_EMISSAO_OPCOES_FILTRO
// abaixo).
const STATUS_EMISSAO_COLORS = {
  NAO_APLICAVEL: null,
  NAO_EMITIDA: 'default',
  PROCESSANDO: 'blue',
  EMITIDO: 'green',
  FALHA_TECNICA: 'red',
  FALHA_PROVIDER: 'red',
  VERIFICACAO_NECESSARIA: 'gold',
};

const STATUS_EMISSAO_LABELS = {
  NAO_APLICAVEL: '—',
  NAO_EMITIDA: 'Não emitida',
  PROCESSANDO: 'Processando',
  EMITIDO: 'Emitido',
  FALHA_TECNICA: 'Falha na emissão',
  FALHA_PROVIDER: 'Falha na emissão',
  VERIFICACAO_NECESSARIA: 'Verificação necessária',
};

const STATUS_COBRANCA_OPCOES_FILTRO = [
  { value: 'SEM_COBRANCA', label: 'Não gerada' },
  { value: 'ABERTA', label: 'Em aberto' },
  { value: 'VENCIDA', label: 'Vencida' },
  { value: 'PAGA', label: 'Paga' },
  // Fase Eventos-Recobranca 3 -- aparece na visao padrao; filtro dedicado.
  { value: 'RECOBRANCA_NECESSARIA', label: 'Recobrança necessária' },
  // O filtro CANCELADA passou a localizar por EXISTENCIA de historico
  // cancelado (auditoria) -- a visao padrao ja mostra a obrigacao como
  // "Recobrança necessária" (ver backend evento_cobranca_admin_service).
  { value: 'CANCELADA', label: 'Com histórico cancelado' },
];

const STATUS_EMISSAO_OPCOES_FILTRO = [
  { value: 'NAO_APLICAVEL', label: 'Não aplicável' },
  { value: 'NAO_EMITIDA', label: 'Não emitida' },
  { value: 'PROCESSANDO', label: 'Processando' },
  { value: 'EMITIDO', label: 'Emitido' },
  { value: 'FALHA_TECNICA', label: 'Falha técnica' },
  { value: 'FALHA_PROVIDER', label: 'Falha no processamento da cobrança' },
  { value: 'VERIFICACAO_NECESSARIA', label: 'Verificação necessária' },
];

// Apresentacao monetaria (Secao 14 da instrucao): `valor` chega como
// STRING Decimal do backend -- Number() aqui e' SOMENTE para formatar a
// exibicao (mesma conversao ja usada em InscricoesEventosAdmin.js/
// AprovacaoComercialModal.js), nunca para logica/calculo financeiro. Nao
// existe utilitario compartilhado de moeda no projeto (auditado: cada
// tela do modulo Eventos formata localmente da mesma forma) -- reaproveita
// exatamente essa convencao em vez de introduzir Intl.NumberFormat (usado
// apenas nas telas de Dashboard, um padrao diferente e nao-compartilhado).
const formatarValorBRL = (valorDecimalString) => {
  const numero = Number(valorDecimalString);
  if (Number.isNaN(numero)) return '—';
  return `R$ ${numero.toFixed(2).replace('.', ',')}`;
};

// Datas do backend chegam como "YYYY-MM-DD" (date, nao datetime) --
// dayjs(...) direto sobre essa string e' o MESMO padrao ja usado sem
// problema em InscricoesEventosAdmin.js (dayjs(item.sessao_data)), nunca
// `new Date(...)` (que interpretaria a string como UTC e poderia
// deslocar o dia exibido em fusos negativos como o do Brasil).
const formatarDataBR = (dataIso) => (dataIso ? dayjs(dataIso).format('DD/MM/YYYY') : '—');

const CobrancasEventosAdmin = () => {
  const { loading: permissaoLoading, isAdmin, podeVisualizar } = usePermissaoRecurso('eventos');

  // Fase 2B: uma linha = uma INSCRICAO (grupo), nao mais uma parcela.
  const [itens, setItens] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [sessoesDoEvento, setSessoesDoEvento] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  // Fase Cobrancas-Operacional 2: resumo/cards PARCELA-based vindos do
  // backend (nunca contados no frontend). Respeitam os filtros contextuais;
  // IGNORAM status_cobranca/status_emissao (fotografia do universo contextual).
  const [resumo, setResumo] = useState(null);

  const [filtroAluno, setFiltroAluno] = useState('');
  const [filtroEvento, setFiltroEvento] = useState(undefined);
  const [filtroSessao, setFiltroSessao] = useState(undefined);
  const [filtroUnidade, setFiltroUnidade] = useState(undefined);
  const [filtroStatusCobranca, setFiltroStatusCobranca] = useState(undefined);
  const [filtroStatusEmissao, setFiltroStatusEmissao] = useState(undefined);
  const [filtroVencimento, setFiltroVencimento] = useState(null);

  // Expansao controlada (Secao 10 da instrucao) -- chave e'
  // `evento_inscricao_id` (estavel, nunca muda). Nao persistido em
  // localStorage.
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Loading/disabled POR OPERACAO+REGISTRO (Fase B.5c.3c.3b, Secao 14 da
  // instrucao) -- nunca trava a tabela inteira. Chaves compostas via
  // `chaveAcaoEmAndamento('gerar'|'emitir'|'verificar', id)`. Isto e'
  // apenas UX (evitar duplo clique) -- o backend continua sendo
  // integralmente responsavel por concorrencia/idempotencia real.
  const [acoesEmAndamento, setAcoesEmAndamento] = useState(() => new Set());

  // Modal de confirmacao de cancelamento (Secao 4 da instrucao) -- item
  // completo (uma PARCELA, nao o grupo) para o modal ter acesso a
  // financial_charge_id ao confirmar. `null` = modal fechado.
  const [modalCancelarItem, setModalCancelarItem] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  // Selecao em lote (Fase 3A.2) -- chave e' `evento_inscricao_id`, MESMA
  // chave da tabela pai. Nunca persiste entre paginas/filtros (Secao 6
  // da instrucao): podada explicitamente em todo ponto que troca o
  // conjunto de resultados (ver `carregar`/paginacao abaixo).
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalLoteVisible, setModalLoteVisible] = useState(false);
  const [loteEmAndamento, setLoteEmAndamento] = useState(false);
  const [resultadoLote, setResultadoLote] = useState(null);

  // Selecao em lote para EMISSAO de boleto (Fase 3B.2) -- chave e'
  // `financial_charge_id` (parcela/Charge, NUNCA evento_inscricao_id),
  // estado GLOBAL (nao por grupo): a selecao pode abranger parcelas de
  // grupos/alunos diferentes na mesma pagina (Secao "UX DE SELECAO" da
  // instrucao). Filtro/paginacao limpam por completo (mesma semantica
  // ja existente de `selectedRowKeys` acima, Secao "REFRESH APOS O
  // LOTE" da instrucao: nunca criar selecao cross-page escondida);
  // qualquer OUTRO refresh (`recarregar` sem `limparSelecao`, inclusive
  // apos o proprio lote de emissao) PODA a selecao via
  // `podarSelecaoEmissaoLote` em vez de limpar por completo -- ver
  // `carregar` abaixo.
  const [chargesSelecionadas, setChargesSelecionadas] = useState([]);
  const [modalEmitirLoteVisible, setModalEmitirLoteVisible] = useState(false);
  const [loteEmissaoEmAndamento, setLoteEmissaoEmAndamento] = useState(false);
  const [resultadoLoteEmissao, setResultadoLoteEmissao] = useState(null);

  // Consolidacao de cobrancas (Fase D.3) -- `modalConsolidarSugestao`
  // e' a sugestao completa (nao so' a chave), permitindo ao modal
  // renderizar os componentes SEM nenhuma chamada de rede adicional
  // (ja' vieram na propria listagem, ver `indexarSugestoesConsolidacao`
  // abaixo). `null` = modal fechado.
  const [modalConsolidarSugestao, setModalConsolidarSugestao] = useState(null);

  const estaAcaoEmAndamento = useCallback(
    (tipo, id) => acoesEmAndamento.has(chaveAcaoEmAndamento(tipo, id)),
    [acoesEmAndamento]
  );

  const iniciarAcao = useCallback((tipo, id) => {
    setAcoesEmAndamento((prev) => {
      const proximo = new Set(prev);
      proximo.add(chaveAcaoEmAndamento(tipo, id));
      return proximo;
    });
  }, []);

  const finalizarAcao = useCallback((tipo, id) => {
    setAcoesEmAndamento((prev) => {
      const proximo = new Set(prev);
      proximo.delete(chaveAcaoEmAndamento(tipo, id));
      return proximo;
    });
  }, []);

  const filtrosAtuais = useCallback(
    () => ({
      nome_aluno: filtroAluno || undefined,
      id_evento: filtroEvento || undefined,
      id_evento_sessao: filtroSessao || undefined,
      id_unidades: filtroUnidade || undefined,
      status_cobranca: filtroStatusCobranca || undefined,
      status_emissao: filtroStatusEmissao || undefined,
      vencimento_de: filtroVencimento?.[0] ? filtroVencimento[0].format('YYYY-MM-DD') : undefined,
      vencimento_ate: filtroVencimento?.[1] ? filtroVencimento[1].format('YYYY-MM-DD') : undefined,
    }),
    [filtroAluno, filtroEvento, filtroSessao, filtroUnidade, filtroStatusCobranca, filtroStatusEmissao, filtroVencimento]
  );

  // `resetExpansao` distingue os dois caminhos da Secao 10 da instrucao:
  // aplicar/limpar filtro SEMPRE reseta a expansao (novo conjunto de
  // resultados); refresh pos-acao financeira e paginacao PRESERVAM a
  // expansao, podada para conter somente grupos que ainda estao na
  // pagina recem-carregada (`chavesExpandidasAposRecarregar`, testada em
  // cobrancasEventosAcoes.test.js). `limparSelecao` (Fase 3A.2, Secao
  // 6/20/21 da instrucao) e' um sinal INDEPENDENTE -- trocar de pagina
  // limpa a selecao mas PRESERVA a expansao podada (comportamentos
  // diferentes um do outro), por isso nao reaproveita `resetExpansao`.
  const carregar = useCallback(
    async (paginaAtual, tamanhoPagina, filtros, { resetExpansao = false, limparSelecao = false } = {}) => {
      setLoading(true);
      try {
        const resp = await eventoCobrancaAdminService.listarAgrupado({
          page: paginaAtual,
          page_size: tamanhoPagina,
          ...filtros,
        });
        const novosItens = resp.data.items || [];
        setItens(novosItens);
        setTotal(resp.data.total || 0);
        setResumo(resp.data.resumo || null);
        setPage(resp.data.page || paginaAtual);
        setPageSize(resp.data.page_size || tamanhoPagina);
        if (resetExpansao) {
          setExpandedRowKeys([]);
        } else {
          const idsPresentes = novosItens.map((grupo) => grupo.evento_inscricao_id);
          setExpandedRowKeys((prev) => chavesExpandidasAposRecarregar(prev, idsPresentes));
        }
        if (limparSelecao) {
          setSelectedRowKeys([]);
          setChargesSelecionadas([]);
        } else {
          // Poda (nunca limpa por completo) apos qualquer OUTRO refresh
          // -- inclusive o refresh pos-lote de emissao: remove somente
          // os `financial_charge_id` que deixaram de existir/deixaram
          // de ter `acao_financeira === EMITIR_BOLETO` na pagina
          // recem-carregada (ex.: uma emissao SUCESSO), Secao "REFRESH
          // APOS O LOTE" da instrucao.
          setChargesSelecionadas((prev) => podarSelecaoEmissaoLote(prev, novosItens));
        }
      } catch (err) {
        handleApiError(err, 'Erro ao carregar cobranças de eventos');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const carregarEventos = useCallback(async () => {
    try {
      const resp = await eventoService.list({ page: 1, page_size: 200 });
      setEventos(resp.data.items || []);
    } catch (err) {
      // Silencioso: so' limita as opcoes do filtro de Evento.
    }
  }, []);

  const carregarUnidades = useCallback(async () => {
    try {
      const resp = await unidadeService.list();
      setUnidades(resp.data || []);
    } catch (err) {
      // Silencioso: so' limita as opcoes do filtro de Unidade.
    }
  }, []);

  // Filtro de Sessao (Secao 12 da instrucao) -- reaproveita a MESMA
  // chamada ja usada por SessoesPainel.js (`eventoService.listSessoes`,
  // page_size 200, o teto de PAGE_SIZE_MAXIMO no backend), nenhuma API
  // nova. Populada somente quando um Evento esta selecionado.
  const carregarSessoesDoEvento = useCallback(async (eventoId) => {
    if (!eventoId) {
      setSessoesDoEvento([]);
      return;
    }
    try {
      const resp = await eventoService.listSessoes(eventoId, { page: 1, page_size: 200 });
      setSessoesDoEvento(resp.data || []);
    } catch (err) {
      // Silencioso: so' limita as opcoes do filtro de Sessao.
    }
  }, []);

  useEffect(() => {
    if (!permissaoLoading && (podeVisualizar || isAdmin)) {
      carregar(1, pageSize, filtrosAtuais(), { resetExpansao: true, limparSelecao: true });
      carregarEventos();
      carregarUnidades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissaoLoading, podeVisualizar, isAdmin]);

  // Trocar o Evento limpa a Sessao selecionada e recarrega as opcoes;
  // limpar o Evento (novoEventoId undefined) tambem limpa a Sessao e as
  // opcoes (Secao 12 da instrucao).
  const handleFiltroEventoChange = (novoEventoId) => {
    setFiltroEvento(novoEventoId);
    setFiltroSessao(undefined);
    carregarSessoesDoEvento(novoEventoId);
  };

  const aplicarFiltros = () => carregar(1, pageSize, filtrosAtuais(), { resetExpansao: true, limparSelecao: true });

  const limparFiltros = () => {
    setFiltroAluno('');
    setFiltroEvento(undefined);
    setFiltroSessao(undefined);
    setSessoesDoEvento([]);
    setFiltroUnidade(undefined);
    setFiltroStatusCobranca(undefined);
    setFiltroStatusEmissao(undefined);
    setFiltroVencimento(null);
    carregar(1, pageSize, {}, { resetExpansao: true, limparSelecao: true });
  };

  const recarregarPaginaAtual = () => carregar(page, pageSize, filtrosAtuais());

  // Consolidacao de cobrancas (Fase D.3) -- recalculados a cada troca
  // de `itens` (refetch/paginacao/filtro), nunca guardados como estado
  // proprio: a listagem e' sempre a fonte de verdade, os indexadores
  // sao PUROS (cobrancasEventosAcoes.js) e operam sobre `grupo.
  // parcelas[]` de TODOS os grupos visuais da pagina atual (cross-
  // evento/cross-card por construcao).
  const sugestoesConsolidacao = useMemo(() => indexarSugestoesConsolidacao(itens), [itens]);
  const gruposConsolidados = useMemo(() => agruparPorGroupId(itens), [itens]);

  const abrirModalConsolidar = (sugestao) => setModalConsolidarSugestao(sugestao);

  const consolidacaoEmAndamento = modalConsolidarSugestao
    ? estaAcaoEmAndamento('formar-grupo', modalConsolidarSugestao.chave_consolidacao)
    : false;

  const fecharModalConsolidar = () => {
    if (consolidacaoEmAndamento) {
      // POST em andamento -- nunca fecha (mesmo principio dos demais
      // modais desta tela).
      return;
    }
    setModalConsolidarSugestao(null);
  };

  // Formacao de grupo (Fase D.3) -- SOMENTE forma o grupo, NUNCA emite
  // o boleto consolidado na mesma chamada (Secao 3 da instrucao: regra
  // de UX fechada, formar e emitir sao operacoes independentes com
  // falhas independentes). Apos sucesso: refetch OBRIGATORIO da
  // listagem -- nunca monta o Group manualmente no estado local a
  // partir da resposta do POST (a listagem/backend e' sempre a fonte
  // de verdade, mesmo principio ja usado por `handleGerarCobrancasLote`/
  // `handleEmitirBoletosLote`).
  const handleConfirmarConsolidacao = async (financialChargeIds) => {
    const chave = modalConsolidarSugestao?.chave_consolidacao;
    iniciarAcao('formar-grupo', chave);
    try {
      await eventoCobrancaAdminService.formarGrupo(financialChargeIds);
      message.success('Cobranças consolidadas com sucesso. Emita o boleto quando estiver pronto.');
      setModalConsolidarSugestao(null);
      await recarregarPaginaAtual();
    } catch (err) {
      const status = err?.response?.status;
      const erro = mensagemErroAcaoFinanceira(
        status,
        'Não foi possível formar a consolidação.',
        'Não foi possível formar a consolidação com os dados atuais.'
      );
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        // 404/409 -- situacao mudou entre a sugestao e o clique (ex.:
        // outra aba ja' agrupou/alterou uma das Charges): fecha o modal
        // (a sugestao exibida ja' nao e' confiavel) e recarrega.
        setModalConsolidarSugestao(null);
        await recarregarPaginaAtual();
      }
      // 422 (dados estruturalmente invalidos) -- mantem o modal aberto,
      // sem refetch, para o admin reavaliar (mesmo padrao dos demais
      // modais desta tela).
    } finally {
      finalizarAcao('formar-grupo', chave);
    }
  };

  // Dissolucao de grupo (Fase D.3) -- somente permitida pelo backend
  // quando o grupo nunca teve nenhuma tentativa de emissao
  // (`acoes_permitidas_grupo` contendo DISSOLVER, ver `grupoTemAcao`).
  // Nunca remove o Group do estado local diretamente -- refetch
  // obrigatorio, mesmo em caso de erro tratado (a listagem e' sempre a
  // fonte de verdade sobre o que ainda existe).
  const handleDissolverGrupo = async (grupo) => {
    const groupId = grupo.id_financial_charge_group;
    iniciarAcao('dissolver-grupo', groupId);
    try {
      await eventoCobrancaAdminService.dissolverGrupo(groupId);
      message.success('Consolidação desfeita com sucesso.');
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(err?.response?.status, 'Não foi possível desfazer esta consolidação.');
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('dissolver-grupo', groupId);
    }
  };

  // Emissao/reemissao do documento consolidado (Fase D.4b) -- MESMO
  // endpoint/handler para os dois: o backend dispatcher decide pela
  // historia canonica do grupo (ver eventoCobrancaAdminService.
  // emitirBoletoGrupo). `grupo.acoes_permitidas_grupo` (EMITIR OU
  // REEMITIR, nunca os dois ao mesmo tempo) e' quem decide qual botao/
  // confirmacao aparece em DocumentoCobrancaConsolidada.js -- este
  // handler nunca precisa saber qual dos dois foi clicado. Apos
  // sucesso OU erro tratado: refetch obrigatorio, nunca monta o
  // documento no estado local a partir da response (mesmo principio de
  // handleDissolverGrupo acima).
  const handleEmitirBoletoGrupo = async (grupo) => {
    const groupId = grupo.id_financial_charge_group;
    iniciarAcao('emitir-grupo', groupId);
    try {
      const resp = await eventoCobrancaAdminService.emitirBoletoGrupo(groupId);
      const feedback = mensagemFeedbackEmissao(resp.data?.status_emissao);
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      // Ajuste pos-auditoria (D.4b) -- refetch SEMPRE, independente do
      // status (404/409/422/503/timeout/erro de rede/ausencia de
      // `response`): um erro HTTP/browser nunca prova que a emissao NAO
      // comecou no backend (o Attempt PENDENTE/ENVIANDO ja pode ter sido
      // commitado antes do erro). Nunca reenvia o POST -- so' consulta
      // GET /agrupado de novo para a tela nunca continuar mostrando um
      // estado potencialmente obsoleto (ex.: "Aguardando emissao" +
      // botao Emitir quando na verdade ja existe uma tentativa em
      // andamento). Mensagem principal da operacao e' exibida ANTES do
      // refetch e nunca substituida por um erro secundario dele --
      // `carregar` (chamada por `recarregarPaginaAtual`) ja isola seu
      // proprio erro via `handleApiError` (nunca propaga), o try/catch
      // aqui e' defesa em profundidade explicita, nao um caminho
      // realmente alcancavel hoje.
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível emitir o boleto consolidado.',
        'Não foi possível emitir: este grupo não está elegível para emissão no momento.'
      );
      message[erro.nivel](erro.texto);
      try {
        await recarregarPaginaAtual();
      } catch (refetchErr) {
        // Falha do refetch nunca mascara/substitui a mensagem da
        // operacao ja exibida acima -- `carregar` ja mostra seu proprio
        // erro (`handleApiError`), nada adicional a fazer aqui.
      }
    } finally {
      finalizarAcao('emitir-grupo', groupId);
    }
  };

  // Recuperacao administrativa de pagamento do documento consolidado
  // (Fase D.4a/D.4b) -- NUNCA cria cobranca nova, so' consulta o
  // provedor e reaplica o pipeline canonico se houver evidencia de
  // pagamento. O 200 desta resposta NUNCA e' tratado como "Pago" por si
  // so' -- o estado real (inclusive PAGO) so' e' afirmado apos o
  // refetch de GET /agrupado que segue esta chamada.
  const handleVerificarPagamentoGrupo = async (grupo) => {
    const groupId = grupo.id_financial_charge_group;
    iniciarAcao('verificar-pagamento-grupo', groupId);
    try {
      const resp = await eventoCobrancaAdminService.verificarPagamentoGrupo(groupId);
      const feedback = mensagemFeedbackVerificarPagamentoGrupo(resp.data?.resultado);
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível verificar o pagamento deste grupo.',
        'Não foi possível verificar: este grupo não está elegível para verificação no momento.'
      );
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('verificar-pagamento-grupo', groupId);
    }
  };

  // Cancelamento do documento consolidado (Fase D.4b) -- cancela
  // SOMENTE o boleto no provedor; as FinancialCharges componentes NUNCA
  // sao alteradas por esta acao (decisao de produto ja fechada no
  // backend, ver CancelarBoletoGrupoResponse). Confirmacao explicita
  // (Popconfirm em DocumentoCobrancaConsolidada.js) ja deixa isso claro
  // ao admin antes do clique chegar aqui.
  const handleCancelarBoletoGrupo = async (grupo) => {
    const groupId = grupo.id_financial_charge_group;
    iniciarAcao('cancelar-grupo', groupId);
    try {
      const resp = await eventoCobrancaAdminService.cancelarBoletoGrupo(groupId);
      const feedback = mensagemFeedbackCancelamento(resp.data?.status_operacao, { contexto: 'cancelar' });
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      // Ajuste pos-auditoria (D.4b) -- mesmo racional de
      // handleEmitirBoletoGrupo acima: refetch SEMPRE, independente do
      // status. Um erro aqui nunca prova que o DELETE no provedor nao
      // foi efetivado -- nunca reenvia o POST de cancelamento, so'
      // consulta GET /agrupado de novo.
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível cancelar o boleto deste grupo.',
        'Não foi possível cancelar: este grupo não está elegível para cancelamento no momento.'
      );
      message[erro.nivel](erro.texto);
      try {
        await recarregarPaginaAtual();
      } catch (refetchErr) {
        // Falha do refetch nunca mascara/substitui a mensagem da
        // operacao ja exibida acima.
      }
    } finally {
      finalizarAcao('cancelar-grupo', groupId);
    }
  };

  // Verificacao/reconciliacao de cancelamento incerto do documento
  // consolidado (Fase D.4b) -- NAO destrutiva, GET-first no backend,
  // NUNCA reenvia DELETE (mesmo principio do individual, ver
  // handleVerificarCancelamento acima). Sem confirmacao -- uma unica
  // chamada por clique.
  const handleVerificarCancelamentoGrupo = async (grupo) => {
    const groupId = grupo.id_financial_charge_group;
    iniciarAcao('verificar-cancelamento-grupo', groupId);
    try {
      const resp = await eventoCobrancaAdminService.verificarCancelamentoGrupo(groupId);
      const feedback = mensagemFeedbackCancelamento(resp.data?.status_operacao, { contexto: 'verificar' });
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível verificar o cancelamento deste grupo.',
        'Não foi possível verificar: solicitação inválida.'
      );
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('verificar-cancelamento-grupo', groupId);
    }
  };

  // Gerar cobrancas (Secao 16 da instrucao): agora e' uma acao de NIVEL
  // DE GRUPO -- a linha pai JA E' a inscricao, entao nao ha mais dedup
  // por "primeira linha" a fazer (ver `grupoElegivelParaGerarCobranca`
  // em cobrancasEventosAcoes.js, que substitui
  // `primeiraLinhaParaGerarCobrancaPorInscricao` nesta tela). Mesmo
  // endpoint por INSCRICAO de sempre, sem geracao em lote.
  const handleGerarCobrancas = async (inscricaoId) => {
    iniciarAcao('gerar', inscricaoId);
    try {
      const resp = await eventoInscricaoAdminService.gerarCobrancas(inscricaoId);
      const criadas = resp.data?.cobrancas_criadas || 0;
      const existentes = resp.data?.cobrancas_ja_existentes || 0;
      if (criadas > 0 && existentes > 0) {
        message.success(
          `${criadas} cobrança${criadas === 1 ? '' : 's'} gerada${criadas === 1 ? '' : 's'}. ` +
            `${existentes} já existia${existentes === 1 ? '' : 'm'}.`
        );
      } else if (criadas > 0) {
        message.success(`${criadas} cobrança${criadas === 1 ? '' : 's'} gerada${criadas === 1 ? '' : 's'} com sucesso.`);
      } else {
        message.success('As cobranças desta inscrição já estavam geradas.');
      }
      await recarregarPaginaAtual();
    } catch (err) {
      const status = err?.response?.status;
      handleApiError(err, 'Não foi possível gerar as cobranças da inscrição.');
      // 409 = pre-condicao mudou entre o render e o clique (ex.: outra
      // aba efetivou/cancelou nesse meio-tempo) -- recarrega para
      // refletir o estado real; outros erros (rede/5xx) nao geram
      // refresh automatico.
      if (status === 409) {
        recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('gerar', inscricaoId);
    }
  };

  // rowSelection (Fase 3A.2) -- elegibilidade reaproveita EXCLUSIVAMENTE
  // `grupoElegivelParaGerarCobranca` (Secao 5 da instrucao), o MESMO
  // sinal ja usado pelo botao "Gerar cobrancas" individual -- nenhuma
  // regra nova, nenhuma inferencia por status_inscricao/status_
  // participacao/status_resumido/quantidade_sem_cobranca/
  // financial_charge_id/provider. `onChange` rejeita (nao trunca)
  // qualquer selecao que ultrapasse o teto do lote, mesmo padrao do
  // precedente `FaturasNfse.js`.
  const rowSelection = {
    selectedRowKeys,
    onChange: (chaves) => {
      if (!selecaoDentroDoLimiteLote(chaves)) {
        message.warning(`Máximo de ${LIMITE_LOTE_GERAR_COBRANCAS} inscrições por lote.`);
        return;
      }
      setSelectedRowKeys(chaves);
    },
    getCheckboxProps: (grupo) => ({
      disabled: !grupoElegivelParaGerarCobranca(grupo),
    }),
  };

  // rowSelection da SUBTABELA de parcelas (Fase 3B.2) -- uma instancia
  // por grupo (fechada sobre `grupo`), mas todas compartilham a MESMA
  // selecao GLOBAL (`chargesSelecionadas`), permitindo selecao cruzando
  // grupos/alunos diferentes (Secao "UX DE SELECAO" da instrucao).
  // Elegibilidade reaproveita EXCLUSIVAMENTE `parcelaElegivelParaEmitirBoletoLote`
  // -- MESMO sinal do botao individual "Emitir boleto". `onChange`
  // rejeita (nunca trunca) qualquer selecao que ultrapasse o teto
  // global do lote, mesmo padrao ja usado pelo rowSelection de
  // materializacao acima.
  const rowSelectionParcelas = (grupo) => {
    // A subtabela usa `rowKey="evento_inscricao_parcela_id"` (identidade
    // de linha, sempre presente e unica mesmo antes da materializacao) --
    // `selectedRowKeys` PRECISA ser expresso NESSE mesmo espaco de id
    // para o antd marcar os checkboxes corretamente, entao traduzimos
    // `chargesSelecionadas` (financial_charge_id) -> evento_inscricao_
    // parcela_id aqui. O `onChange` faz o caminho inverso usando as
    // LINHAS completas (segundo argumento), nunca as chaves brutas.
    const parcelaIdPorChargeId = new Map(
      (grupo?.parcelas || [])
        .filter((parcela) => parcela.financial_charge_id != null)
        .map((parcela) => [parcela.financial_charge_id, parcela.evento_inscricao_parcela_id])
    );
    const selectedRowKeys = chargesSelecionadas
      .filter((id) => parcelaIdPorChargeId.has(id))
      .map((id) => parcelaIdPorChargeId.get(id));

    return {
      selectedRowKeys,
      onChange: (_chavesDoGrupo, linhasSelecionadasDoGrupo) => {
        const idsSelecionadosDoGrupo = linhasSelecionadasDoGrupo
          .filter(parcelaElegivelParaEmitirBoletoLote)
          .map((parcela) => parcela.financial_charge_id);
        const foraDoGrupo = chargesSelecionadas.filter((id) => !idsElegiveisDoGrupo(grupo).includes(id));
        const novaSelecao = [...foraDoGrupo, ...idsSelecionadosDoGrupo];
        if (!selecaoDentroDoLimiteLote(novaSelecao, LIMITE_LOTE_EMITIR_BOLETOS)) {
          message.warning(`Máximo de ${LIMITE_LOTE_EMITIR_BOLETOS} cobranças por lote.`);
          return;
        }
        setChargesSelecionadas(novaSelecao);
      },
      getCheckboxProps: (parcela) => ({
        disabled: !parcelaElegivelParaEmitirBoletoLote(parcela),
      }),
    };
  };

  // Conveniencia "selecionar elegiveis do grupo" (Secao "SELECIONAR
  // ELEGIVEIS DO GRUPO" da instrucao) -- regra de produto NAO
  // negociavel: se completar a selecao do grupo ultrapassar o teto
  // global, a operacao inteira e' recusada (nunca parcial/truncada).
  const handleSelecionarElegiveisDoGrupo = (grupo) => {
    const resultado = selecionarElegiveisDoGrupo(chargesSelecionadas, grupo, LIMITE_LOTE_EMITIR_BOLETOS);
    if (resultado.recusado) {
      message.warning(
        `É possível emitir no máximo ${LIMITE_LOTE_EMITIR_BOLETOS} boletos por lote. Você já possui ` +
          `${chargesSelecionadas.length} selecionado${chargesSelecionadas.length === 1 ? '' : 's'}. Selecione manualmente as cobranças desejadas.`
      );
      return;
    }
    setChargesSelecionadas(resultado.selecao);
  };

  const handleDesmarcarTodasDoGrupo = (grupo) => {
    setChargesSelecionadas((prev) => desmarcarTodasDoGrupo(prev, grupo));
  };

  const abrirModalLote = () => {
    setResultadoLote(null);
    setModalLoteVisible(true);
  };

  const fecharModalLote = () => {
    if (loteEmAndamento) {
      // POST em andamento -- nunca fecha (mesmo principio do modal de
      // cancelamento: evita reabrir e disparar uma segunda chamada).
      return;
    }
    setModalLoteVisible(false);
    setResultadoLote(null);
  };

  // Materializacao em lote (Fase 3A.2) -- UMA chamada ao endpoint de
  // lote (Fase 3A.1, ja mergeado), NUNCA um loop client-side sobre
  // `handleGerarCobrancas`. Contrato de resposta usado EXATAMENTE como
  // o backend devolve (Secao 13 da instrucao) -- nenhuma contagem
  // recalculada aqui.
  const handleGerarCobrancasLote = async () => {
    setLoteEmAndamento(true);
    try {
      const resp = await eventoInscricaoAdminService.gerarCobrancasLote(selectedRowKeys);
      setResultadoLote(resp.data);
      // Sucesso (mesmo com falha PARCIAL -- o endpoint de lote sempre
      // responde 200 estruturado, ver Fase 3A.1): limpa selecao e
      // atualiza a listagem a partir do backend (Secao 16 da instrucao,
      // nunca reconstroi grupos localmente). Expansao preservada/podada
      // normalmente (resetExpansao nao passado).
      await carregar(page, pageSize, filtrosAtuais(), { limparSelecao: true });
    } catch (err) {
      // Erro HTTP GLOBAL (a request inteira falhou, nunca um resultado
      // parcial) -- Secao 22 da instrucao: mensagem unica, MANTEM a
      // selecao para permitir retry, sem retry automatico, sem fingir
      // sucesso parcial. `resultadoLote` permanece `null`, entao o modal
      // volta ao passo de confirmacao (nunca mostra um resumo falso).
      handleApiError(err, 'Não foi possível gerar as cobranças selecionadas.');
    } finally {
      setLoteEmAndamento(false);
    }
  };

  const abrirModalEmitirLote = () => {
    setResultadoLoteEmissao(null);
    setModalEmitirLoteVisible(true);
  };

  const fecharModalEmitirLote = () => {
    if (loteEmissaoEmAndamento) {
      // POST em andamento -- nunca fecha (mesmo principio dos demais
      // modais desta tela: evita reabrir e disparar uma segunda
      // chamada antes da primeira responder).
      return;
    }
    setModalEmitirLoteVisible(false);
    setResultadoLoteEmissao(null);
  };

  // Emissao de boleto EM LOTE (Fase 3B.2, backend Fase 3B.1 ja
  // mergeado) -- EXATAMENTE UMA chamada a `emitirBoletosLote`, NUNCA um
  // loop/Promise.all client-side sobre `handleEmitirBoleto` (Secao
  // "MODAL DE CONFIRMACAO" da instrucao: proibido). Contrato de
  // resposta usado EXATAMENTE como o backend devolve (5 status reais,
  // nenhuma contagem recalculada aqui).
  const handleEmitirBoletosLote = async () => {
    setLoteEmissaoEmAndamento(true);
    try {
      const resp = await eventoCobrancaAdminService.emitirBoletosLote(chargesSelecionadas);
      setResultadoLoteEmissao(resp.data);
      // Sucesso (mesmo com falha PARCIAL/incerta -- o endpoint de lote
      // sempre responde 200 estruturado, Fase 3B.1): recarrega a
      // listagem a partir do backend para obter o estado atualizado e
      // os identificadores (linha digitavel/codigo de barras) que a
      // resposta do lote deliberadamente nao inclui. `limparSelecao`
      // NAO e' passado aqui de proposito -- o refresh PODA a selecao
      // (via `podarSelecaoEmissaoLote` dentro de `carregar`), nunca a
      // limpa por completo, preservando qualquer id que ainda continue
      // elegivel apos o lote.
      await carregar(page, pageSize, filtrosAtuais());
    } catch (err) {
      // Erro HTTP GLOBAL (a request inteira falhou, nunca um resultado
      // parcial) -- mesma semantica ja' usada por `handleGerarCobrancasLote`:
      // mensagem unica, MANTEM a selecao para permitir retry, sem retry
      // automatico, sem fingir sucesso parcial. `resultadoLoteEmissao`
      // permanece `null`, entao o modal volta ao passo de confirmacao.
      handleApiError(err, 'Não foi possível emitir os boletos selecionados.');
    } finally {
      setLoteEmissaoEmAndamento(false);
    }
  };

  // Emissao de boleto (Fase B.5c.3c.1) -- sem body: financial_charge_id
  // vem SOMENTE da URL (ver eventoCobrancaAdminService.emitirBoleto).
  // Apos a resposta (sucesso OU erro que indique mudanca de estado),
  // recarrega a listagem para que o backend (fonte de verdade) reflita
  // o estado atualizado -- nunca mutamos a linha manualmente aqui.
  const handleEmitirBoleto = async (item) => {
    const chargeId = item.financial_charge_id;
    iniciarAcao('emitir', chargeId);
    try {
      const resp = await eventoCobrancaAdminService.emitirBoleto(chargeId);
      const feedback = mensagemFeedbackEmissao(resp.data?.status_emissao);
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(err?.response?.status, 'Não foi possível emitir o boleto.');
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('emitir', chargeId);
    }
  };

  // Verificacao/reconciliacao de emissao incerta (Fase B.5c.3c.2) --
  // NUNCA cria uma nova cobranca, apenas consulta/reconcilia uma
  // tentativa ja existente. Sem body: financial_charge_id vem SOMENTE
  // da URL.
  const handleVerificarEmissao = async (item) => {
    const chargeId = item.financial_charge_id;
    iniciarAcao('verificar', chargeId);
    try {
      const resp = await eventoCobrancaAdminService.verificarEmissao(chargeId);
      const feedback = mensagemFeedbackVerificacao(resp.data?.status_emissao);
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(err?.response?.status, 'Não foi possível verificar a situação desta cobrança.');
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('verificar', chargeId);
    }
  };

  // Cancelamento (Fase cancelamento-canonico) -- clicar no botao apenas
  // ABRE o modal, NUNCA chama a API diretamente (Secao 4 da instrucao).
  // `cancelarEmAndamento` (loading por Charge) e' derivado de
  // `modalCancelarItem`, nao de estado proprio, para o modal e o botao
  // da tabela compartilharem a MESMA fonte de loading. `modalCancelarItem`
  // continua sendo uma PARCELA (Secao 17 da instrucao), agora vinda de
  // dentro de `grupo.parcelas` em vez de uma linha plana.
  const cancelarEmAndamento = modalCancelarItem
    ? estaAcaoEmAndamento('cancelar', modalCancelarItem.financial_charge_id)
    : false;

  const abrirModalCancelar = (item) => {
    setModalCancelarItem(item);
    setMotivoCancelamento('');
  };

  const fecharModalCancelar = () => {
    if (cancelarEmAndamento) {
      // POST em andamento -- nunca fecha (evita reabrir o modal e
      // disparar uma segunda chamada antes da primeira responder).
      return;
    }
    setModalCancelarItem(null);
    setMotivoCancelamento('');
  };

  // Apos qualquer resposta definitiva do backend (sucesso semantico OU
  // erro HTTP capturado no catch), o modal e' fechado -- a chamada ja'
  // foi feita e respondida, reabrir o mesmo modal para "tentar de novo"
  // nao faz sentido: o admin deve reavaliar a linha ja' recarregada.
  const handleConfirmarCancelamento = async () => {
    const item = modalCancelarItem;
    if (!item) return;
    const chargeId = item.financial_charge_id;
    iniciarAcao('cancelar', chargeId);
    try {
      const resp = await eventoCobrancaAdminService.cancelar(chargeId, motivoCancelamento.trim());
      const feedback = mensagemFeedbackCancelamento(resp.data?.status_operacao, {
        statusCharge: resp.data?.status_charge,
        contexto: 'cancelar',
      });
      message[feedback.nivel](feedback.texto);
      setModalCancelarItem(null);
      setMotivoCancelamento('');
      // Se este refresh falhar, `carregar` trata o proprio erro
      // separadamente (handleApiError com mensagem propria) -- nunca
      // reescreve o feedback de cancelamento ja exibido acima.
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível cancelar esta cobrança.',
        'Não foi possível cancelar: o motivo informado não é válido.'
      );
      message[erro.nivel](erro.texto);
      setModalCancelarItem(null);
      setMotivoCancelamento('');
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('cancelar', chargeId);
    }
  };

  // Verificacao/reconciliacao de cancelamento incerto (Fase
  // cancelamento-canonico) -- NAO destrutiva, GET-first no backend,
  // NUNCA reenvia DELETE, sem confirmacao, uma unica chamada por clique.
  const handleVerificarCancelamento = async (item) => {
    const chargeId = item.financial_charge_id;
    iniciarAcao('verificar_cancelamento', chargeId);
    try {
      const resp = await eventoCobrancaAdminService.verificarCancelamento(chargeId);
      const feedback = mensagemFeedbackCancelamento(resp.data?.status_operacao, {
        statusCharge: resp.data?.status_charge,
        contexto: 'verificar',
      });
      message[feedback.nivel](feedback.texto);
      await recarregarPaginaAtual();
    } catch (err) {
      const erro = mensagemErroAcaoFinanceira(
        err?.response?.status,
        'Não foi possível verificar o cancelamento desta cobrança.',
        'Não foi possível verificar o cancelamento: solicitação inválida.'
      );
      message[erro.nivel](erro.texto);
      if (erro.deveRecarregar) {
        await recarregarPaginaAtual();
      }
    } finally {
      finalizarAcao('verificar_cancelamento', chargeId);
    }
  };

  const handleCopiarLinhaDigitavel = async (linhaDigitavel) => {
    try {
      await navigator.clipboard.writeText(linhaDigitavel);
      message.success('Linha digitável copiada.');
    } catch (err) {
      message.error('Não foi possível copiar a linha digitável.');
    }
  };

  // Fase E.1 -- abre o WhatsApp com a mensagem ja' pronta (mesmo padrao
  // ja usado em FaturasList.js: wa.me + encodeURIComponent, nova aba).
  // 100% client-side -- NENHUMA chamada HTTP, NENHUMA mutacao
  // financeira, NENHUM registro de "enviado" (fora de escopo desta
  // fase, ver investigacao E.1). `telefone`/`mensagem` ja' vem prontos
  // do chamador (DocumentoCobrancaConsolidada.js/coluna individual) --
  // este handler nunca decide elegibilidade nem monta texto.
  const handleEnviarWhatsApp = ({ telefone, mensagem }) => {
    const whatsappUrl = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`;
    const janela = window.open(whatsappUrl, '_blank', 'noopener');
    if (!janela) {
      message.warning('Não foi possível abrir o WhatsApp. Verifique bloqueio de pop-up.');
    }
  };

  // Colunas da SUBTABELA de parcelas (Secao 5 da instrucao) -- gerada por
  // grupo (closure sobre `grupo.quantidade_parcelas`, que so' existe no
  // NIVEL DO GRUPO: `ParcelaCobrancaAgrupadaResponse` nao tem
  // `total_parcelas` proprio, ao contrario da visao plana). Renderizacoes
  // e acoes financeiras reaproveitadas ao maximo da tela plana original,
  // operando sobre `parcela` (mesmo shape de item da visao plana).
  const colunasParcelas = (grupo) => [
    {
      title: 'Parcela',
      key: 'parcela',
      render: (_, parcela) => `${parcela.numero_parcela}/${grupo.quantidade_parcelas ?? '?'}`,
    },
    {
      title: 'Valor',
      key: 'valor',
      render: (_, parcela) => formatarValorBRL(parcela.valor),
    },
    {
      title: 'Vencimento',
      key: 'vencimento',
      render: (_, parcela) => formatarDataBR(parcela.data_vencimento),
    },
    {
      title: 'Cobrança',
      key: 'status_cobranca',
      render: (_, parcela) => (
        <Space size={4} wrap>
          <Tag color={STATUS_COBRANCA_COLORS[parcela.status_cobranca] || 'default'}>
            {STATUS_COBRANCA_LABELS[parcela.status_cobranca] || parcela.status_cobranca}
          </Tag>
          {parcela.alerta_participacao_cancelada_com_cobranca_aberta && (
            <Tooltip title="Participação cancelada — cobrança permanece aberta.">
              <WarningOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
          {/* Fase D.3 -- indicador discreto, sem botao (a interface
              principal de consolidacao e' a secao acima da tabela, ver
              Secao 25 da instrucao: "nao poluir visualmente"). */}
          {parcela.id_financial_charge_group != null ? (
            <Tag>Consolidada</Tag>
          ) : (
            parcela.chave_consolidacao && <Tag>Pode ser consolidada</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Emissão',
      key: 'status_emissao',
      render: (_, parcela) =>
        parcela.status_emissao === 'NAO_APLICAVEL' || !parcela.status_emissao ? (
          '—'
        ) : (
          <Tag color={STATUS_EMISSAO_COLORS[parcela.status_emissao] || 'default'}>
            {STATUS_EMISSAO_LABELS[parcela.status_emissao] || parcela.status_emissao}
          </Tag>
        ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, parcela) => {
        // Fonte semantica EXCLUSIVA da acao primaria: parcela.acao_financeira
        // (Secao 5 da instrucao B.5c.3c.3b) -- nunca status_emissao/
        // status_cobranca/status_participacao/status_inscricao/provider/
        // data_vencimento/linha_digitavel. "Gerar cobrancas" NAO aparece
        // mais aqui -- e' acao de nivel de grupo (Secao 16 da instrucao).
        const mostrarBotaoEmitir =
          parcela.acao_financeira === ACAO_FINANCEIRA.EMITIR_BOLETO && Boolean(parcela.financial_charge_id);
        const mostrarBotaoVerificar =
          parcela.acao_financeira === ACAO_FINANCEIRA.VERIFICAR_EMISSAO && Boolean(parcela.financial_charge_id);
        const mostrarBotaoCancelar = mostrarBotaoCancelarCobranca(parcela);
        const mostrarBotaoVerificarCancelamento =
          parcela.acao_financeira === ACAO_FINANCEIRA.VERIFICAR_CANCELAMENTO && Boolean(parcela.financial_charge_id);
        // Fase Eventos-Recobranca 3 -- acao INDIVIDUAL de recobranca (a
        // unica cobranca da parcela foi cancelada). Usa o MESMO endpoint
        // por inscricao (POST .../gerar-cobrancas, autoridade da Fase 2),
        // NUNCA um endpoint novo. Confirmacao explicita (Passo 6). NUNCA
        // aparece no fluxo em massa (Passo 7 -- ver grupoElegivelParaGerar
        // Cobranca, que exige SEM_COBRANCA).
        const mostrarBotaoGerarNovaCobranca = parcelaElegivelParaRecobranca(parcela);
        const gerarEmAndamento = estaAcaoEmAndamento('gerar', grupo.evento_inscricao_id);

        const emitirEmAndamento = estaAcaoEmAndamento('emitir', parcela.financial_charge_id);
        const verificarEmAndamento = estaAcaoEmAndamento('verificar', parcela.financial_charge_id);
        const cancelarDestaLinhaEmAndamento = estaAcaoEmAndamento('cancelar', parcela.financial_charge_id);
        const verificarCancelamentoEmAndamento = estaAcaoEmAndamento('verificar_cancelamento', parcela.financial_charge_id);

        // Fase E.1 -- WhatsApp INDIVIDUAL. `responsavel_telefone` vive
        // no `grupo` (nivel da inscricao, mesmo aluno/responsavel para
        // todas as parcelas), nunca por parcela. Autorizacao via
        // `parcelaElegivelParaWhatsApp` (status_emissao === 'EMITIDO',
        // NUNCA reconstruido de outros campos, mesmo principio de
        // `acao_financeira` acima) -- nunca aparece para uma parcela
        // agrupada (Charge de Group nunca tem Attempt/url_boleto/
        // linha_digitavel proprios, sempre null, ver docstring de
        // financial_charge_group_documento_identificadores_service.py).
        const telefoneWhatsApp = normalizarTelefoneWhatsApp(grupo.responsavel_telefone);
        const whatsAppElegivel = parcelaElegivelParaWhatsApp(parcela, telefoneWhatsApp);
        const documentoEmitido = parcela.status_emissao === 'EMITIDO';

        return (
          <Space size={4} wrap>
            {mostrarBotaoGerarNovaCobranca && (
              <Popconfirm
                title="Gerar nova cobrança?"
                description={
                  <div style={{ maxWidth: 320 }}>
                    A cobrança anterior desta parcela foi <b>cancelada</b>. Será criada uma
                    {' '}<b>nova cobrança</b> para a mesma parcela. A cobrança cancelada
                    {' '}<b>permanece no histórico</b> (não é reaberta nem excluída).
                  </div>
                }
                onConfirm={() => handleGerarCobrancas(grupo.evento_inscricao_id)}
                okText="Gerar nova cobrança"
                cancelText="Cancelar"
              >
                <Button size="small" type="primary" loading={gerarEmAndamento} disabled={gerarEmAndamento}>
                  Gerar nova cobrança
                </Button>
              </Popconfirm>
            )}
            {mostrarBotaoEmitir && (
              <Popconfirm
                title="Emitir este boleto?"
                description="A cobrança será enviada ao provedor financeiro configurado para a unidade."
                onConfirm={() => handleEmitirBoleto(parcela)}
                okText="Emitir"
                cancelText="Cancelar"
              >
                <Button size="small" type="primary" loading={emitirEmAndamento} disabled={emitirEmAndamento}>
                  Emitir boleto
                </Button>
              </Popconfirm>
            )}
            {mostrarBotaoVerificar && (
              <Tooltip title="Consulta a situação de uma tentativa de emissão já existente, sem criar nova cobrança.">
                <Button
                  size="small"
                  loading={verificarEmAndamento}
                  disabled={verificarEmAndamento}
                  onClick={() => handleVerificarEmissao(parcela)}
                >
                  Verificar situação
                </Button>
              </Tooltip>
            )}
            {mostrarBotaoCancelar && (
              <Button
                size="small"
                danger
                loading={cancelarDestaLinhaEmAndamento}
                disabled={cancelarDestaLinhaEmAndamento}
                onClick={() => abrirModalCancelar(parcela)}
              >
                Cancelar cobrança
              </Button>
            )}
            {mostrarBotaoVerificarCancelamento && (
              <Tooltip title="Consulta a situação de uma tentativa de cancelamento já existente, sem reenviar o cancelamento.">
                <Button
                  size="small"
                  loading={verificarCancelamentoEmAndamento}
                  disabled={verificarCancelamentoEmAndamento}
                  onClick={() => handleVerificarCancelamento(parcela)}
                >
                  Verificar cancelamento
                </Button>
              </Tooltip>
            )}
            {parcela.linha_digitavel && (
              <Tooltip title="Copiar linha digitável">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopiarLinhaDigitavel(parcela.linha_digitavel)}
                />
              </Tooltip>
            )}
            {documentoEmitido && !whatsAppElegivel && !telefoneWhatsApp && (
              <Tooltip title="Responsável financeiro sem telefone cadastrado.">
                <span>
                  <Button size="small" icon={<WhatsAppOutlined />} disabled>
                    Enviar por WhatsApp
                  </Button>
                </span>
              </Tooltip>
            )}
            {whatsAppElegivel && (
              <Button
                size="small"
                icon={<WhatsAppOutlined />}
                onClick={() =>
                  handleEnviarWhatsApp({
                    telefone: telefoneWhatsApp,
                    mensagem: construirMensagemWhatsAppIndividual({
                      alunoNome: grupo.aluno_nome,
                      eventoNome: grupo.evento_nome,
                      valorFormatado: formatarValorBRL(parcela.valor),
                      vencimentoFormatado: formatarDataBR(parcela.data_vencimento),
                      urlBoleto: parcela.url_boleto,
                      linhaDigitavel: parcela.linha_digitavel,
                    }),
                  })
                }
              >
                Enviar por WhatsApp
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // Colunas da tabela PAI (Secao 4 da instrucao) -- uma linha = uma
  // inscricao. Enxuta de proposito (responsividade, Secao 19): so' o
  // essencial para decidir se vale expandir, o detalhe por parcela fica
  // na subtabela.
  const columns = [
    {
      title: 'Aluno',
      key: 'aluno',
      render: (_, grupo) => grupo.aluno_nome || '—',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
    },
    {
      title: 'Evento / Sessão',
      key: 'evento',
      responsive: ['md', 'lg', 'xl', 'xxl'],
      render: (_, grupo) => (
        <div>
          <div>{grupo.evento_nome}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatarDataBR(grupo.sessao_data)}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Valor total',
      key: 'valor_total',
      responsive: ['lg', 'xl', 'xxl'],
      render: (_, grupo) => (grupo.valor_total != null ? formatarValorBRL(grupo.valor_total) : '—'),
    },
    {
      title: 'Parcelas',
      key: 'parcelas_resumo',
      render: (_, grupo) => (
        <div>
          <Space size={4}>
            <Tag color={corStatusResumido(grupo.status_resumido)}>{labelStatusResumido(grupo.status_resumido)}</Tag>
            {grupo.parcelas?.some((p) => p.alerta_participacao_cancelada_com_cobranca_aberta) && (
              <Tooltip title="Ao menos uma parcela: participação cancelada, cobrança permanece aberta.">
                <WarningOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
          </Space>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {resumoCompactoParcelas(grupo)}
            </Typography.Text>
          </div>
          {grupo.status_inscricao === 'APROVADA' && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Aguardando efetivação
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, grupo) => {
        // "Gerar cobrancas" no NIVEL DO GRUPO (Secao 16 da instrucao) --
        // exatamente uma vez por inscricao, nunca por parcela. Fonte
        // semantica exclusiva: `grupoElegivelParaGerarCobranca` (funcao
        // pura testada em cobrancasEventosAcoes.test.js).
        const mostrarBotaoGerar = grupoElegivelParaGerarCobranca(grupo);
        const gerarEmAndamento = estaAcaoEmAndamento('gerar', grupo.evento_inscricao_id);
        return (
          mostrarBotaoGerar && (
            <Popconfirm
              title="Gerar as cobranças desta inscrição?"
              description="Serão geradas as cobranças pendentes de todas as parcelas desta inscrição."
              onConfirm={() => handleGerarCobrancas(grupo.evento_inscricao_id)}
              okText="Gerar"
              cancelText="Cancelar"
            >
              <Tooltip title="Gera as cobranças pendentes de todas as parcelas desta inscrição.">
                <Button size="small" type="primary" loading={gerarEmAndamento} disabled={gerarEmAndamento}>
                  Gerar cobranças
                </Button>
              </Tooltip>
            </Popconfirm>
          )
        );
      },
    },
  ];

  if (!permissaoLoading && !podeVisualizar && !isAdmin) {
    return (
      <Card>
        <Typography.Text type="secondary">Você não tem permissão para visualizar Cobranças de Eventos.</Typography.Text>
      </Card>
    );
  }

  return (
    <Card
      title="Cobranças de Eventos"
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Acompanhe as cobranças das inscrições em eventos.
        </Typography.Text>
      }
    >
      {/* Cards / indicadores (Fase Cobrancas-Operacional 2) -- PARCELA-based,
          vindos do backend (resp.data.resumo). Respeitam os filtros
          contextuais (Evento/Sessao/Unidade/Aluno/Vencimento); NAO sao
          afetados por status_cobranca/status_emissao nem pela pagina.
          Informativos (nao clicaveis) para nao arriscar o componente. */}
      <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
        <Col flex="1 1 130px">
          <Card size="small"><Statistic title="Em aberto" value={(resumo && resumo.ABERTA) || 0} /></Card>
        </Col>
        <Col flex="1 1 130px">
          <Card size="small">
            <Statistic title="Vencidas" value={(resumo && resumo.VENCIDA) || 0}
              valueStyle={{ color: (resumo && resumo.VENCIDA) ? '#cf1322' : undefined }} />
          </Card>
        </Col>
        <Col flex="1 1 130px">
          <Card size="small">
            <Statistic title="Pagas" value={(resumo && resumo.PAGA) || 0}
              valueStyle={{ color: (resumo && resumo.PAGA) ? '#3f8600' : undefined }} />
          </Card>
        </Col>
        <Col flex="1 1 130px">
          <Card size="small"><Statistic title="Sem cobrança" value={(resumo && resumo.SEM_COBRANCA) || 0} /></Card>
        </Col>
        {/* Fase Eventos-Recobranca 3 -- 5o card. Parcelas cuja unica
            cobranca foi cancelada e precisam de uma nova (recobranca). */}
        <Col flex="1 1 130px">
          <Card size="small">
            <Statistic title="Recobrança necessária"
              value={(resumo && resumo.RECOBRANCA_NECESSARIA) || 0}
              valueStyle={{ color: (resumo && resumo.RECOBRANCA_NECESSARIA) ? '#d46b08' : undefined }} />
          </Card>
        </Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col flex="1 1 200px">
          <Card size="small">
            <Statistic title="Valor a receber"
              value={formatarValorBRL((resumo && resumo.valor_a_receber) ?? '0')} />
          </Card>
        </Col>
        <Col flex="1 1 200px">
          <Card size="small">
            <Statistic title="Valor recebido"
              value={formatarValorBRL((resumo && resumo.valor_recebido) ?? '0')}
              valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Buscar por aluno"
          value={filtroAluno}
          onChange={(e) => setFiltroAluno(e.target.value)}
          onPressEnter={aplicarFiltros}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          placeholder="Evento"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={filtroEvento}
          onChange={handleFiltroEventoChange}
          options={eventos.map((e) => ({ value: e.id, label: e.nome }))}
        />
        <Select
          placeholder="Sessão"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={filtroSessao}
          onChange={setFiltroSessao}
          disabled={!filtroEvento}
          options={sessoesDoEvento.map((s) => ({ value: s.id, label: `${s.nome} — ${formatarDataBR(s.data)}` }))}
        />
        <Select
          placeholder="Unidade"
          allowClear
          style={{ width: 180 }}
          value={filtroUnidade}
          onChange={setFiltroUnidade}
          options={unidades.map((u) => ({ value: u.id, label: u.nome_fantasia }))}
        />
        <Select
          placeholder="Status da cobrança"
          allowClear
          style={{ width: 170 }}
          value={filtroStatusCobranca}
          onChange={setFiltroStatusCobranca}
          options={STATUS_COBRANCA_OPCOES_FILTRO}
        />
        <Select
          placeholder="Status da emissão"
          allowClear
          style={{ width: 220 }}
          value={filtroStatusEmissao}
          onChange={setFiltroStatusEmissao}
          options={STATUS_EMISSAO_OPCOES_FILTRO}
        />
        <RangePicker
          placeholder={['Vencimento de', 'Vencimento até']}
          value={filtroVencimento}
          onChange={setFiltroVencimento}
          format="DD/MM/YYYY"
        />
        <Button type="primary" onClick={aplicarFiltros}>
          Filtrar
        </Button>
        <Button onClick={limparFiltros}>Limpar</Button>
      </Space>

      <SecaoCobrancasConsolidadas
        sugestoes={sugestoesConsolidacao}
        grupos={gruposConsolidados}
        onVerEConsolidar={abrirModalConsolidar}
        onDissolver={handleDissolverGrupo}
        onEmitir={handleEmitirBoletoGrupo}
        onVerificarPagamento={handleVerificarPagamentoGrupo}
        onCancelar={handleCancelarBoletoGrupo}
        onVerificarCancelamento={handleVerificarCancelamentoGrupo}
        onCopiarLinhaDigitavel={handleCopiarLinhaDigitavel}
        onEnviarWhatsApp={handleEnviarWhatsApp}
        estaAcaoEmAndamento={estaAcaoEmAndamento}
      />

      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={abrirModalLote}>
            Gerar cobranças selecionadas ({selectedRowKeys.length})
          </Button>
        </Space>
      )}

      {chargesSelecionadas.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={abrirModalEmitirLote} disabled={loteEmissaoEmAndamento}>
            Emitir boletos selecionados ({chargesSelecionadas.length})
          </Button>
          <Typography.Text type="secondary">
            Total selecionado: {formatarValorBRL(calcularValorSelecionadoEmissaoLote(itens, chargesSelecionadas))}
          </Typography.Text>
        </Space>
      )}

      <Table
        rowKey="evento_inscricao_id"
        loading={loading || permissaoLoading}
        dataSource={itens}
        columns={columns}
        scroll={{ x: 720 }}
        rowSelection={rowSelection}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (chaves) => setExpandedRowKeys(chaves),
          expandedRowRender: (grupo) => {
            const elegiveisDoGrupo = idsElegiveisDoGrupo(grupo);
            const selecionadosDoGrupo = chargesSelecionadas.filter((id) => elegiveisDoGrupo.includes(id));
            return (
              <div>
                {elegiveisDoGrupo.length > 0 && (
                  <Space style={{ marginBottom: 8 }}>
                    <Button size="small" onClick={() => handleSelecionarElegiveisDoGrupo(grupo)}>
                      Selecionar elegíveis desta inscrição
                    </Button>
                    {selecionadosDoGrupo.length > 0 && (
                      <Button size="small" onClick={() => handleDesmarcarTodasDoGrupo(grupo)}>
                        Desmarcar desta inscrição
                      </Button>
                    )}
                  </Space>
                )}
                <Table
                  rowKey="evento_inscricao_parcela_id"
                  dataSource={grupo.parcelas}
                  columns={colunasParcelas(grupo)}
                  pagination={false}
                  scroll={{ x: 720 }}
                  rowSelection={rowSelectionParcelas(grupo)}
                />
              </div>
            );
          },
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t} inscriç${t === 1 ? 'ão' : 'ões'}`,
          // Trocar pagina/tamanho de pagina limpa a selecao (Secao 21 da
          // instrucao -- MVP nunca persiste selecao entre paginas), mas
          // preserva a expansao podada (resetExpansao nao passado aqui,
          // mesmo comportamento de antes da Fase 3A.2).
          onChange: (novaPagina, novoTamanho) =>
            carregar(novaPagina, novoTamanho, filtrosAtuais(), { limparSelecao: true }),
        }}
      />

      <ConsolidarCobrancasModal
        open={Boolean(modalConsolidarSugestao)}
        sugestao={modalConsolidarSugestao}
        emAndamento={consolidacaoEmAndamento}
        onConfirmar={handleConfirmarConsolidacao}
        onFechar={fecharModalConsolidar}
      />

      <Modal
        title="Cancelar cobrança"
        open={Boolean(modalCancelarItem)}
        onOk={handleConfirmarCancelamento}
        onCancel={fecharModalCancelar}
        okText="Confirmar cancelamento"
        cancelText="Voltar"
        okButtonProps={{ danger: true, loading: cancelarEmAndamento, disabled: cancelarEmAndamento }}
        cancelButtonProps={{ disabled: cancelarEmAndamento }}
        closable={!cancelarEmAndamento}
        maskClosable={!cancelarEmAndamento}
        destroyOnClose
      >
        <Typography.Paragraph>
          Esta cobrança será cancelada. Se já houver um boleto emitido, o cancelamento também será solicitado ao
          provedor financeiro.
        </Typography.Paragraph>
        <Typography.Text>Motivo do cancelamento (opcional)</Typography.Text>
        <Input.TextArea
          value={motivoCancelamento}
          onChange={(e) => setMotivoCancelamento(e.target.value)}
          maxLength={2000}
          showCount
          rows={3}
          disabled={cancelarEmAndamento}
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        title="Gerar cobranças selecionadas"
        open={modalLoteVisible}
        onCancel={fecharModalLote}
        closable={!loteEmAndamento}
        maskClosable={!loteEmAndamento}
        width={640}
        footer={
          resultadoLote ? (
            <Button onClick={fecharModalLote}>Fechar</Button>
          ) : [
            <Button key="cancelar" onClick={fecharModalLote} disabled={loteEmAndamento}>
              Cancelar
            </Button>,
            <Button
              key="confirmar"
              type="primary"
              loading={loteEmAndamento}
              disabled={loteEmAndamento}
              onClick={handleGerarCobrancasLote}
            >
              Gerar cobranças
            </Button>,
          ]
        }
      >
        {!resultadoLote && !loteEmAndamento && (
          <div>
            <Typography.Paragraph>
              Você está prestes a gerar cobranças para {selectedRowKeys.length} inscriç
              {selectedRowKeys.length === 1 ? 'ão' : 'ões'}.
            </Typography.Paragraph>
            <Typography.Paragraph>
              Serão materializadas até {calcularQuantidadeEstimadaLote(itens, selectedRowKeys)} cobranças.
            </Typography.Paragraph>
            <Alert
              type="info"
              showIcon
              message="Nenhum boleto será emitido nesta etapa. Somente as cobranças serão registradas."
            />
          </div>
        )}

        {loteEmAndamento && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Gerando cobranças..." />
          </div>
        )}

        {resultadoLote && !loteEmAndamento && (
          <div>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color="blue">Solicitadas: {resultadoLote.total_solicitado}</Tag>
              <Tag color="green">Sucesso: {resultadoLote.total_sucesso}</Tag>
              {resultadoLote.total_ja_existente > 0 && (
                <Tag color="default">Já existentes: {resultadoLote.total_ja_existente}</Tag>
              )}
              {resultadoLote.total_ignorado > 0 && (
                <Tag color="gold">Ignoradas: {resultadoLote.total_ignorado}</Tag>
              )}
              {resultadoLote.total_erro > 0 && <Tag color="red">Erros: {resultadoLote.total_erro}</Tag>}
            </Space>

            {loteConcluidoSemProblemas(resultadoLote) ? (
              <Alert type="success" showIcon message="Todas as cobranças selecionadas foram processadas com sucesso." />
            ) : (
              <Table
                size="small"
                rowKey="evento_inscricao_id"
                dataSource={resultadosProblematicosLote(resultadoLote)}
                columns={[
                  { title: 'Inscrição', dataIndex: 'evento_inscricao_id', width: 100 },
                  { title: 'Status', dataIndex: 'status', width: 100 },
                  { title: 'Código', dataIndex: 'codigo', width: 220 },
                  { title: 'Mensagem', dataIndex: 'mensagem' },
                ]}
                pagination={false}
              />
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Emitir boletos selecionados"
        open={modalEmitirLoteVisible}
        onCancel={fecharModalEmitirLote}
        closable={!loteEmissaoEmAndamento}
        maskClosable={!loteEmissaoEmAndamento}
        width={640}
        footer={
          resultadoLoteEmissao ? (
            <Button onClick={fecharModalEmitirLote}>Fechar</Button>
          ) : [
            <Button key="cancelar" onClick={fecharModalEmitirLote} disabled={loteEmissaoEmAndamento}>
              Cancelar
            </Button>,
            <Button
              key="confirmar"
              type="primary"
              loading={loteEmissaoEmAndamento}
              disabled={loteEmissaoEmAndamento}
              onClick={handleEmitirBoletosLote}
            >
              Emitir boletos
            </Button>,
          ]
        }
      >
        {!resultadoLoteEmissao && !loteEmissaoEmAndamento && (
          <div>
            <Typography.Paragraph>
              Você está prestes a emitir {chargesSelecionadas.length} boleto
              {chargesSelecionadas.length === 1 ? '' : 's'}, totalizando{' '}
              {formatarValorBRL(calcularValorSelecionadoEmissaoLote(itens, chargesSelecionadas))}.
            </Typography.Paragraph>
            <Alert
              type="info"
              showIcon
              message="As cobranças serão enviadas ao provedor financeiro configurado para cada unidade."
            />
          </div>
        )}

        {loteEmissaoEmAndamento && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Emitindo boletos..." />
          </div>
        )}

        {resultadoLoteEmissao && !loteEmissaoEmAndamento && (
          <div>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color="blue">Solicitadas: {resultadoLoteEmissao.total_solicitado}</Tag>
              <Tag color="green">Emitidas agora: {resultadoLoteEmissao.total_sucesso}</Tag>
              {resultadoLoteEmissao.total_ja_emitido > 0 && (
                <Tag color="default">Já emitidas: {resultadoLoteEmissao.total_ja_emitido}</Tag>
              )}
              {resultadoLoteEmissao.total_ignorado > 0 && (
                <Tag color="gold">Ignoradas: {resultadoLoteEmissao.total_ignorado}</Tag>
              )}
              {resultadoLoteEmissao.total_incerto > 0 && (
                <Tag color="orange">Incertas: {resultadoLoteEmissao.total_incerto}</Tag>
              )}
              {resultadoLoteEmissao.total_erro > 0 && <Tag color="red">Erros: {resultadoLoteEmissao.total_erro}</Tag>}
            </Space>

            {resultadoLoteEmissao.total_incerto > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Algumas emissões ficaram com resultado incerto. Verifique a situação de cada cobrança antes de tentar novamente."
              />
            )}

            {loteEmissaoConcluidoSemProblemas(resultadoLoteEmissao) ? (
              <Alert type="success" showIcon message="Todos os boletos selecionados foram processados com sucesso." />
            ) : (
              <Table
                size="small"
                rowKey="financial_charge_id"
                dataSource={resultadosProblematicosLoteEmissao(resultadoLoteEmissao)}
                columns={[
                  { title: 'Cobrança', dataIndex: 'financial_charge_id', width: 100 },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 140,
                    render: (status) => <Tag color={corStatusItemLoteEmissao(status)}>{labelStatusItemLoteEmissao(status)}</Tag>,
                  },
                  { title: 'Código', dataIndex: 'codigo', width: 200 },
                  { title: 'Mensagem', dataIndex: 'mensagem' },
                ]}
                pagination={false}
              />
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default CobrancasEventosAdmin;
